import { useState } from "react";
import { Card, CardHeader, CardBody, PageError, PageLoading } from "../components/index.js";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { downloadFile, safeHttpUrl } from "./AgencyWork.jsx";

/**
 * Client portal "Work" tab (Phase 4d). Read-only status of the work orders
 * BMG is delivering for this client, plus the deliverables they can open,
 * download, and approve. Backed by /api/portal/work — scoped to the JWT's
 * client; tasks, assignees, notes and internal deliverables never arrive here.
 */
const STATUS = {
  "In progress": { bg: "#eff6ff", fg: "#2563eb" },
  Delivered:     { bg: "#fbeae0", fg: "#8a4b1a" },
  Scheduled:     { bg: "#f5f5f5", fg: "#525252" },
  Complete:      { bg: "#f0fdf4", fg: "#16a34a" },
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }) : "");

export function PortalWork() {
  const { data, loading, error, reload } = useApi("/api/portal/work");
  const api = useApiAction();
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);
  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading && !data) return <PageLoading />;
  const orders = data?.orders || [];
  async function approve(d) {
    setBusyId(d.id); setErr(null);
    try { await api(`/api/portal/deliverables/${d.id}/approve`, { method: "POST" }); await reload({ silent: true }); }
    catch (e) { setErr(e.message); } finally { setBusyId(null); }
  }
  return (
    <div className="max-w-[820px] space-y-4">
      {err && <p className="text-[12px] text-negative">{err}</p>}
      {orders.length === 0 ? (
        <Card><CardHeader title="Work" subtitle="What we're working on and what's ready" /><CardBody className="py-8 text-center text-[13px] text-text-muted">Nothing in progress right now.</CardBody></Card>
      ) : orders.map((o) => {
        const c = STATUS[o.status] ?? STATUS.Scheduled;
        return (
          <Card key={o.id}>
            <CardHeader title={o.title} subtitle={o.period ? `Period ${o.period}` : o.dueDate ? `Target ${fmtDay(o.dueDate)}` : undefined} right={<span className="inline-flex items-center h-5 px-2 rounded text-[11px] font-medium" style={{ backgroundColor: c.bg, color: c.fg }}>{o.status}</span>} />
            {o.deliverables.length > 0 && (
              <CardBody className="text-[13px]">
                <ul className="divide-y divide-border-light">
                  {o.deliverables.map((d) => {
                    const safeUrl = safeHttpUrl(d.url);
                    return (
                      <li key={d.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="font-medium text-text-primary">{d.title}</div>
                          <div className="text-[12px] text-text-secondary">{d.status === "approved" ? `Approved ${fmt(d.approvedAt)}` : `Delivered ${fmt(d.deliveredAt)}`}</div>
                        </div>
                        <div className="flex items-center gap-3 text-[12px]">
                          {safeUrl && <a href={safeUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">Open</a>}
                          {d.hasFile && <button type="button" onClick={() => downloadFile(`/api/portal/deliverables/${d.id}/file`, d.fileName).catch((e) => setErr(e.message))} className="text-accent hover:underline">Download</button>}
                          {d.status === "delivered" && <button type="button" disabled={busyId === d.id} onClick={() => approve(d)} className="h-8 px-3 rounded-card bg-accent text-white font-medium disabled:opacity-50">{busyId === d.id ? "Approving…" : "Approve"}</button>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            )}
          </Card>
        );
      })}
    </div>
  );
}
